import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import { Button } from '../components/ui/Button';
import {
  Trophy,
  Target,
  Clock,
  Zap,
  Star,
  Crown,
  History,
  RotateCcw,
  BookOpen,
  Library,
  Gamepad2,
  TrendingUp,
} from 'lucide-react';

interface MatchResult {
  id: string;
  deckId?: string;
  deckName?: string;
  date: string;
  moves: number;
  mistakes: number;
  time: number;
  accuracy: number;
  totalPairs: number;
}

const MATCH_STORAGE_KEY = 'flashcard-match-results';

const loadMatchResults = (): MatchResult[] => {
  try {
    const data = localStorage.getItem(MATCH_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const AccomplishmentsPage: React.FC = () => {
  const { decks, sessions } = useAppState();
  const navigate = useNavigate();
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setMatchResults(loadMatchResults());
  }, []);

  const totalCards = decks.reduce((acc, d) => acc + d.cards.length, 0);
  const matchGamesPlayed = matchResults.length;
  const perfectGames = matchResults.filter((r) => r.accuracy === 100).length;
  const averageAccuracy = matchGamesPlayed > 0
    ? Math.round(matchResults.reduce((acc, r) => acc + r.accuracy, 0) / matchGamesPlayed)
    : 0;
  const totalTimeStudying = matchResults.reduce((acc, r) => acc + r.time, 0);
  const bestAccuracy = matchResults.length > 0
    ? Math.max(...matchResults.map((r) => r.accuracy))
    : 0;
  const fastestTime = matchResults.length > 0
    ? Math.min(...matchResults.filter((r) => r.accuracy === 100).map((r) => r.time)) || null
    : null;

  const recentResults = matchResults.slice(0, 5);
  const allTimeBest = matchResults.length > 0
    ? matchResults.reduce((best, r) => (r.accuracy > best.accuracy ? r : best), matchResults[0])
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Accomplishments
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Your learning journey at a glance
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
          <History className="w-4 h-4 mr-2" />
          {showHistory ? 'Hide' : 'Full'} History
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-5 border border-blue-100 dark:border-blue-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Library className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Decks</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{decks.length}</div>
          <div className="text-xs text-gray-500 mt-1">{totalCards} total cards</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-5 border border-purple-100 dark:border-purple-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Match Games</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{matchGamesPlayed}</div>
          <div className="text-xs text-gray-500 mt-1">{perfectGames} perfect games</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl p-5 border border-green-100 dark:border-green-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">Avg Accuracy</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{averageAccuracy}%</div>
          <div className="text-xs text-gray-500 mt-1">Best: {bestAccuracy}%</div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl p-5 border border-amber-100 dark:border-amber-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Study Time</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{formatTime(totalTimeStudying)}</div>
          <div className="text-xs text-gray-500 mt-1">in match games</div>
        </div>
      </div>

      {/* All-Time Best & Recent */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* All-Time Best */}
        {allTimeBest && (
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">All-Time Best</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{allTimeBest.accuracy}%</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {allTimeBest.moves} pairs in {formatTime(allTimeBest.time)}
                </div>
                {allTimeBest.deckName && (
                  <div className="text-xs text-gray-400 mt-1">{allTimeBest.deckName}</div>
                )}
                <div className="text-xs text-gray-400">{formatDate(allTimeBest.date)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Fastest Perfect */}
        {fastestTime !== null && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Fastest Perfect Game</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">{formatTime(fastestTime)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">100% accuracy</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Results */}
      {recentResults.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Recent Match Games
          </h2>
          <div className="space-y-3">
            {recentResults.map((result, index) => (
              <div
                key={result.id}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    result.accuracy === 100
                      ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : result.accuracy >= 80
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                        : result.accuracy >= 60
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                  }`}>
                    {result.accuracy === 100 ? (
                      <Star className="w-5 h-5 fill-current" />
                    ) : (
                      <span className="text-sm font-bold">#{index + 1}</span>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {result.deckName || 'Unknown Deck'}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {result.moves} pairs · {result.mistakes} mistakes
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xl font-bold ${
                    result.accuracy === 100
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}>
                    {result.accuracy}%
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatTime(result.time)}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {formatDateTime(result.date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {matchGamesPlayed === 0 && (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Trophy className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Accomplishments Yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Play the matching game to start tracking your progress and earn achievements!
          </p>
          <Button onClick={() => navigate('/study')} leftIcon={<Gamepad2 className="w-4 h-4" />}>
            Start Playing
          </Button>
        </div>
      )}

      {/* Full History */}
      {showHistory && matchResults.length > 5 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-gray-500" />
            Full History ({matchResults.length} games)
          </h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {matchResults.map((result) => (
              <div
                key={result.id}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    result.accuracy === 100
                      ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  }`}>
                    {result.accuracy === 100 ? <Star className="w-4 h-4 fill-current" /> : '•'}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">{result.deckName || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-gray-500 dark:text-gray-400">{formatDate(result.date)}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100 w-12 text-right">{result.accuracy}%</span>
                  <span className="text-gray-500 dark:text-gray-400 w-16 text-right">{formatTime(result.time)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={() => navigate('/study')} leftIcon={<Gamepad2 className="w-4 h-4" />}>
          Play Match Game
        </Button>
      </div>
    </div>
  );
};

export default AccomplishmentsPage;